<script setup>import { ref as _ref } from 'vue';

  import { computed, onMounted, watch } from "vue";
  import DropdownWidget from "./dropdown-widget.vue";

  const TYPES = [
    { key: "subrecipe", label: "Subrecipes" },
    { key: "food", label: "Food" },
    { key: "labor", label: "Labor/Hourly" },
    { key: "packaging", label: "Packaging/Other" },
    { key: "safety", label: "Safety" },
  ];

  const props = defineProps({
    modelValue: { type: Array, default: () => [] },
    // Type keys to hide. Costs passes ["safety"] -- safety ingredients are never
    // costed (Ingredient#costed? == !safety?), so the box could never match a row.
    exclude: { type: Array, default: () => [] },
  });

  const emit = defineEmits(["update:model-value"]);

  const componentRef = _ref(null);

  const visibleTypes = computed(() => TYPES.filter((t) => !props.exclude.includes(t.key)));

  // External `[]` ("no filter") displays as zero checked, so picking a single
  // type is one click. UI state is always literal -- no auto-expansion.
  let modelValue = _ref([...props.modelValue]);

  // The parent (Pinia store / mixin) often bounces our own emit back as a prop change.
  // Without this guard, an emit of all-checked normalized to `[]` would round-trip
  // through the parent and clobber the user's visible state.
  let ignoreNextPropChange = false;
  watch(
    () => props.modelValue,
    (next) => {
      if (ignoreNextPropChange) {
        ignoreNextPropChange = false;
        return;
      }
      modelValue.value = [...next];
    }
  );

  const isChecked = (key) => modelValue.value.includes(key);
  const allChecked = computed(() => modelValue.value.length === visibleTypes.value.length);

  const triggerLabel = computed(() => {
    if (modelValue.value.length === 0 || allChecked.value) return "All types";
    return visibleTypes.value
      .filter((t) => modelValue.value.includes(t.key))
      .map((t) => t.label)
      .join(", ");
  });

  const setSelection = (next) => {
    modelValue.value = next;
    ignoreNextPropChange = true;
    // All-checked and none-checked both mean "no filter" -- collapse to [] at emit so
    // consumers (store / mixin) can stay key-list-agnostic and the URL stays clean.
    const normalized = next.length === visibleTypes.value.length ? [] : next;
    emit("update:model-value", normalized);
  };

  const toggle = (key) => {
    const next = modelValue.value.includes(key)
      ? modelValue.value.filter((k) => k !== key)
      : [...modelValue.value, key];
    setSelection(next);
  };

  const reset = () => {
    setSelection([]);
  };

  // Vanilla-JS callers (ingredient_filters_mixin used by costs/allergens/recipe-edit)
  // push state changes (e.g. from popstate) via dispatchEvent on the web-component element,
  // which is componentRef.parentNode. Mirrors the pattern in tag-input.vue.
  onMounted(() => {
    componentRef.value?.parentNode?.addEventListener("external-update:model-value", (event) => {
      modelValue.value = [...(event.detail?.[0] ?? [])];
    });
  });
</script>

<template>
  <div ref="componentRef" class="ingredient-type-filter-v">
    <DropdownWidget>
      <template #trigger>
        <button type="button" class="button">
          <span class="icon is-left">
            <i class="fa fa-filter"></i>
          </span>
          <span>{{ triggerLabel }}</span>
          <span class="icon is-small">
            <i class="fas fa-angle-down"></i>
          </span>
        </button>
      </template>

      <template #content>
        <div class="dropdown-item">
          <label v-for="t in visibleTypes" :key="t.key" class="checkbox is-block">
            <input
              type="checkbox"
              class="mr-1"
              :checked="isChecked(t.key)"
              @change="toggle(t.key)"
            />
            {{ t.label }}
          </label>
          <button
            v-if="modelValue.length > 0"
            type="button"
            class="button is-small is-fullwidth is-light mt-2"
            @click="reset"
          >
            Reset
          </button>
        </div>
      </template>
    </DropdownWidget>
  </div>
</template>
